import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { authenticatedGet } from '../utils/api';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Typography, Button, CircularProgress, Alert, Chip, Tooltip, Box
} from '@mui/material';
import {
  CheckCircle, Schedule, Error as ErrorIcon, CloudUpload, 
  AutoStories, Psychology, Summarize, Star
} from '@mui/icons-material';
import { useBook } from '../Context/BookContext';
import { Book } from '../types';
import { useAuth } from '../Context/AuthContext';

const getUsersBooksEndpoint = process.env.REACT_APP_LAMBDA_GET_USERS_BOOKS;

interface BookTableProps {
  onSelectBook: (book: Book) => void;
}

const BookTable: React.FC<BookTableProps> = ({ onSelectBook }) => {
  const { selectedBook, refreshTrigger, lastUploadedBook, setSelectedBook } = useBook();
  const lastUploadedBookRef = React.useRef(lastUploadedBook);
  const { user, isAuthenticated } = useAuth();
  const [files, setFiles] = useState<Book[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [pollingActive, setPollingActive] = useState<boolean>(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const startPollingRef = useRef<((isDeletionPolling?: boolean) => void) | null>(null);
  const fetchFilesRef = useRef<(() => Promise<void>) | null>(null);
  const initialLoadDoneRef = useRef<boolean>(false);
  const autoResumeProcessedRef = useRef<boolean>(false);

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      console.log("Stopping polling interval");
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setPollingActive(false);
    // Reset auto-resume flag when polling stops so it can resume again if needed
    autoResumeProcessedRef.current = false;
  }, []);

  const fetchFiles = useCallback(async (): Promise<void> => {
    if (!isAuthenticated || !user?.userId) {
      setFiles([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      console.log(`Fetching all books for user: ${user.userId}`);
      const response = await authenticatedGet<{books: Book[]}>(getUsersBooksEndpoint!);
      
      if (response.data?.books && response.data.books.length > 0) {
        console.log(`Received ${response.data.books.length} books`, 
          response.data.books.map(b => ({ id: b.FileId, title: b.Title })));
        setFiles(response.data.books);
        
        // Check if ANY books are still processing
        const processingBooks = response.data.books.filter(book => {
          const status = book.ProcessingStatus;
          return status && ![
            'completed',
            'error_upload',
            'error_info', 
            'error_summary',
            'error_subjects',
            'error_interesting'
          ].includes(status);
        });

        if (processingBooks.length === 0) {
          // Don't stop polling immediately if we just uploaded a book
          // The book might not have its processing status set yet
          console.log('No processing books check:', {
            hasRecentUpload: !!lastUploadedBookRef.current,
            recentUploadId: (lastUploadedBookRef.current as any)?.FileId
          });
          
          if (!lastUploadedBookRef.current) {
            console.log("No books are currently processing, stopping polling");
            stopPolling();
          } else {
            console.log("No processing books found, but recently uploaded - will wait for polling to start");
          }
        } else {
          console.log(`${processingBooks.length} book(s) still processing:`, 
            processingBooks.map(book => `${book.FileName} (${book.ProcessingStatus})`).join(', '));
        }

      } else {
        console.warn('No books returned from GET_USERS_BOOKS API');
        setFiles([]);
        // Always stop polling if no books are returned
        stopPolling();
      }
      
      // Set initialLoadDone after the first load
      initialLoadDoneRef.current = true;
    } catch (error) {
      console.error('Error fetching files:', error);
      if (axios.isAxiosError(error)) {
        console.error('Error details:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status
        });
      }
      setError('Failed to load books. Please try again.');
    } finally {
      setIsLoading(false);
    }
      }, [stopPolling, isAuthenticated, user?.userId]);
  
  // Store reference to fetchFiles
  fetchFilesRef.current = fetchFiles;
  
  const startPolling = useCallback((isDeletionPolling = false) => {
    stopPolling();

    if (!isAuthenticated || !user?.userId) {
      return;
    }

    console.log(`Starting polling every 15 seconds${isDeletionPolling ? ' (monitoring deletion)' : ' (monitoring all processing books)'}`);
    
    // Start immediately, then every 15 seconds
    fetchFiles().catch(console.error);
    
    const interval = setInterval(() => {
      fetchFiles().catch(console.error);
    }, 15000);
    
    intervalRef.current = interval;
    setPollingActive(true);
  }, [stopPolling, fetchFiles, isAuthenticated, user?.userId]);
  
  // Store reference to startPolling
  startPollingRef.current = startPolling;

  // Stop polling interval on unmount
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  // Keep ref in sync with lastUploadedBook
  useEffect(() => {
    lastUploadedBookRef.current = lastUploadedBook;
  }, [lastUploadedBook]);

  // Add effect to fetch files when refreshTrigger changes
  useEffect(() => {
    fetchFiles();
  }, [fetchFiles, refreshTrigger]);

  // Auto-resume polling for any processing books when page loads/refreshes
  // (but don't auto-select - let UploadStage handle that)
  useEffect(() => {
    if (files.length > 0 && initialLoadDoneRef.current && !pollingActive && !autoResumeProcessedRef.current) {
      // Find the most recent book that's still processing
      const processingBooks = files.filter(book => {
        const status = book.ProcessingStatus;
        return status && ![
          'completed',
          'error_upload',
          'error_info', 
          'error_summary',
          'error_subjects',
          'error_interesting'
        ].includes(status);
      });

      if (processingBooks.length > 0) {
        console.log(`Found ${processingBooks.length} processing book(s) after page load:`, 
          processingBooks.map(book => `${book.FileName} (${book.ProcessingStatus})`).join(', '));
        console.log('Resuming polling for all processing books');
        autoResumeProcessedRef.current = true;
        startPolling();
      }
    }
  }, [files, pollingActive, startPolling]);

  // Update selected book when files array changes (without triggering refetch)
  useEffect(() => {
    if (selectedBook && files.length > 0) {
      console.log(`Checking for updates to selected book: ${selectedBook.Title} (${selectedBook.FileId})`);
      const updatedBook = files.find(file => file.FileId === selectedBook.FileId);
      if (updatedBook) {
        // Update selected book if processing status changed
        if (updatedBook.ProcessingStatus !== selectedBook.ProcessingStatus) {
          console.log('Book processing status changed, updating selection');
          onSelectBook(updatedBook);
        }
      } else {
        console.log(`Selected book with FileId: ${selectedBook.FileId} no longer exists - clearing selection`);
        setSelectedBook(null);
      }
    }
  }, [files, selectedBook, onSelectBook, setSelectedBook]);

  const handleSelectBook = (fileId: string): void => {
    const selectedBook = files.find(file => file.FileId === fileId);
    if (selectedBook) {
      onSelectBook(selectedBook);
    }
  };


  // Function to get processing status display information
  const getProcessingStatusInfo = (book: Book) => {
    const status = book.ProcessingStatus || 'uploaded';
    
    switch (status) {
      case 'uploaded':
        return {
          icon: <CloudUpload sx={{ fontSize: 16 }} />,
          text: 'Uploaded',
          color: 'text.disabled', // gray
          severity: 'default' as const
        };
      case 'processing_upload':
        return {
          icon: <CloudUpload sx={{ fontSize: 16 }} />,
          text: 'Processing Upload',
          color: 'warning.main', // amber
          severity: 'info' as const
        };
      case 'waiting_for_info':
        return {
          icon: <Schedule sx={{ fontSize: 16 }} />,
          text: 'Queued for Analysis',
          color: 'warning.main', // amber
          severity: 'info' as const
        };
      case 'processing_info':
        return {
          icon: <AutoStories sx={{ fontSize: 16 }} />,
          text: 'Extracting Info',
          color: 'primary.main', // blue
          severity: 'info' as const
        };
      case 'waiting_for_summary':
        return {
          icon: <Schedule sx={{ fontSize: 16 }} />,
          text: 'Queued for Summary',
          color: 'warning.main', // amber
          severity: 'info' as const
        };
      case 'processing_summary':
        return {
          icon: <Summarize sx={{ fontSize: 16 }} />,
          text: 'Creating Summary',
          color: 'primary.main', // blue
          severity: 'info' as const
        };
      case 'waiting_for_subjects':
        return {
          icon: <Schedule sx={{ fontSize: 16 }} />,
          text: 'Queued for Subjects',
          color: 'warning.main', // amber
          severity: 'info' as const
        };
      case 'processing_subjects':
        return {
          icon: <Psychology sx={{ fontSize: 16 }} />,
          text: 'Finding Subjects',
          color: 'secondary.main', // purple
          severity: 'info' as const
        };
      case 'waiting_for_interesting':
        return {
          icon: <Schedule sx={{ fontSize: 16 }} />,
          text: 'Queued for Final Step',
          color: 'warning.main', // amber
          severity: 'info' as const
        };
      case 'finding_interesting_subjects':
        return {
          icon: <Star sx={{ fontSize: 16 }} />,
          text: 'Finding Best Subjects',
          color: 'secondary.main', // purple
          severity: 'info' as const
        };
      case 'completed':
        return {
          icon: <CheckCircle sx={{ fontSize: 16 }} />,
          text: 'Ready',
          color: 'success.main', // green
          severity: 'success' as const
        };
      case 'error_upload':
        return {
          icon: <ErrorIcon sx={{ fontSize: 16 }} />,
          text: 'Upload Error',
          color: 'error.main', // red
          severity: 'error' as const
        };
      case 'error_info':
        return {
          icon: <ErrorIcon sx={{ fontSize: 16 }} />,
          text: 'Info Error',
          color: 'error.main', // red
          severity: 'error' as const
        };
      case 'error_summary':
        return {
          icon: <ErrorIcon sx={{ fontSize: 16 }} />,
          text: 'Summary Error',
          color: 'error.main', // red
          severity: 'error' as const
        };
      case 'error_subjects':
        return {
          icon: <ErrorIcon sx={{ fontSize: 16 }} />,
          text: 'Subjects Error',
          color: 'error.main', // red
          severity: 'error' as const
        };
      case 'error_interesting':
        return {
          icon: <ErrorIcon sx={{ fontSize: 16 }} />,
          text: 'Final Step Error',
          color: 'error.main', // red
          severity: 'error' as const
        };
      default:
        return {
          icon: <Schedule sx={{ fontSize: 16 }} />,
          text: 'Unknown',
          color: 'text.disabled', // gray
          severity: 'default' as const
        };
    }
  };

  // Function to get detailed tooltip descriptions for processing status
  const getStatusTooltip = (status: string) => {
    switch (status) {
      case 'uploaded':
        return 'Book has been uploaded and is waiting to start processing';
      case 'processing_upload':
        return 'Processing the uploaded file and storing metadata';
      case 'waiting_for_info':
        return 'Waiting in queue for book information extraction to begin';
      case 'processing_info':
        return 'Extracting book information, parsing content, and creating chapters';
      case 'waiting_for_summary':
        return 'Waiting in queue for summary generation to begin';
      case 'processing_summary':
        return 'Analyzing content and creating intelligent summaries using AI';
      case 'waiting_for_subjects':
        return 'Waiting in queue for subject extraction to begin';
      case 'processing_subjects':
        return 'Identifying characters, objects, and interesting subjects for image generation';
      case 'waiting_for_interesting':
        return 'Waiting in queue for final processing step to begin';
      case 'finding_interesting_subjects':
        return 'Ranking and selecting the most interesting subjects for visualization';
      case 'completed':
        return 'Book is fully processed and ready for reading and image generation';
      case 'error_upload':
        return 'Failed to process uploaded file - there may be an issue with the upload';
      case 'error_info':
        return 'Failed to extract book information - there may be an issue with the file format';
      case 'error_summary':
        return 'Failed to create summaries - processing may need to be retried';
      case 'error_subjects':
        return 'Failed to extract subjects - image generation may be limited';
      case 'error_interesting':
        return 'Failed to find interesting subjects - final processing step needs retry';
      default:
        return 'Processing status information';
    }
  };

  return (
    <TableContainer component={Paper}>
      {/* Action Buttons with proper spacing */}
      <Box sx={{ p: 2, pb: 1 }}>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={fetchFiles}
        >
          Refresh Book List
        </Button>
      </Box>
      
      {error && (
        <Alert 
          severity={error.startsWith('Successfully') ? 'success' : 'error'} 
          sx={{ mb: 2, mt: 1 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}
      
      {isLoading ? (
        <Typography variant="body1" color="textSecondary">
          Loading...
        </Typography>
      ) : files.length === 0 ? (
        <Typography variant="body1" color="textSecondary">
          No books uploaded yet.
        </Typography>
      ) : (
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><Typography variant="h6">Book Name</Typography></TableCell>
              <TableCell><Typography variant="h6">Generated Images</Typography></TableCell>
              <TableCell><Typography variant="h6">Status</Typography></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {files.map((file) => (
              <TableRow 
                key={file.FileId}
                onClick={() => handleSelectBook(file.FileId)}
                sx={{
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'action.hover'
                  },
                  backgroundColor: selectedBook?.FileId === file.FileId ? 'action.selected' : 'inherit'
                }}
              >
                <TableCell>{file.Title || 'Unnamed Book'}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {file.ImageCount || 0}
                    </Typography>
                    {file.RecommendedQuantity && (
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        / {file.RecommendedQuantity} recommended
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  {(() => {
                    const statusInfo = getProcessingStatusInfo(file);
                            const isProcessing = file.ProcessingStatus &&
          ['processing_upload', 'waiting_for_info', 'processing_info', 'waiting_for_summary', 'processing_summary', 'waiting_for_subjects', 'processing_subjects', 'waiting_for_interesting', 'finding_interesting_subjects'].includes(file.ProcessingStatus);
                    
                    return (
                      <Tooltip
                        title={getStatusTooltip(file.ProcessingStatus || 'uploaded')}
                        placement="top"
                      >
                        <Chip
                          icon={isProcessing ? <CircularProgress size={14} sx={{ color: 'inherit !important' }} /> : statusInfo.icon}
                          label={statusInfo.text}
                          size="small"
                          sx={{
                            backgroundColor: statusInfo.color,
                            color: '#fff',
                            fontWeight: 500,
                            '& .MuiChip-icon': {
                              color: '#fff !important'
                            },
                            ...(isProcessing && {
                              animation: 'pulse 2s infinite',
                              '@keyframes pulse': {
                                '0%': {
                                  opacity: 1,
                                  transform: 'scale(1)',
                                },
                                '50%': {
                                  opacity: 0.8,
                                  transform: 'scale(1.02)',
                                },
                                '100%': {
                                  opacity: 1,
                                  transform: 'scale(1)',
                                },
                              }
                            })
                          }}
                        />
                      </Tooltip>
                    );
                  })()}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </TableContainer>
  );
};

export default BookTable; 